'use client';

import type { PropsWithChildren } from 'react';
import { partition } from 'lodash';
import { type AuthenticatedUser } from '@frameio/graphql-client/generated';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import { EMPTY_ARRAY } from '@frameio/utils';
import { MenuGroup, MenuNonInteractiveArea, Text } from '@frameio/vapor';
import { AccountOption } from './AccountOption';
import { V4_ACCOUNT_VERSION } from './constants';
import TEXT from './generated/TEXT/AccountSwitcherList';
import { useGetAllAccountsForPrimaryNavPanelQuery } from './gql/queries/generated/GetAllAccountsForPrimaryNavPanel';
import type { onAccountChangedFn } from './types';

TEXT.DEFINE({
  /** ARIA label for the list of selectable accounts in the account switcher menu */
  ariaLabelForAccountMenu: 'Account Option Menu',
  /** Tooltip label for the primary-nav account switcher button */
  accountSwitcherTooltip: 'Switch Accounts',
  /** Section header shown above accounts that are not supported in this client */
  unsupportedAccounts: 'Unsupported Accounts',
  /** Helper text explaining how to access unsupported accounts from the legacy app/extension menu */
  unsupportedAccountsDescription:
    'Go to Window > Frame.io > Frame.io (Legacy) to access the following accounts.',
});

const MENU_HEIGHT_WITHOUT_LIST = '268px';

const getCalculatedHeight = (maxHeight: number): string => {
  return `calc(${maxHeight}vh - ${MENU_HEIGHT_WITHOUT_LIST})`;
};

export const AccountSwitcherList = (
  props: PropsWithChildren<{
    activeAccountId: string;
    onAccountChanged: onAccountChangedFn;
    setIsShowing: (isShowing: boolean) => void;
  }>
) => {
  const { data } = useGetAllAccountsForPrimaryNavPanelQuery();
  const isIntegrationClient = useIsIntegrationClient();
  const me = data?.me as AuthenticatedUser | undefined;

  const otherAccounts =
    me?.accounts?.nodes?.map((node) => node.account) ?? EMPTY_ARRAY;
  const inboxAccounts =
    me?.inboxAccounts?.nodes?.filter(
      (
        node
      ): node is typeof node & {
        id: string;
      } => node.id != null
    ) ?? EMPTY_ARRAY;
  const allAccounts = [...otherAccounts, ...inboxAccounts];

  const [supportedAccounts, unsupportedAccounts] = isIntegrationClient
    ? partition(
        allAccounts ?? EMPTY_ARRAY,
        (account) => account.version === V4_ACCOUNT_VERSION
      )
    : [allAccounts ?? EMPTY_ARRAY, EMPTY_ARRAY];

  if (!data) {
    return <>{props.children}</>;
  }

  return (
    <>
      <MenuGroup
        aria-label={TEXT.ariaLabelForAccountMenu}
        data-testid="account-modal"
        maxHeight={getCalculatedHeight(75)}
      >
        {/** Show options once other accounts have been loaded */}
        {supportedAccounts &&
          supportedAccounts.map((account, index) => (
            <AccountOption
              key={`account-settings-popover-item-${account.id}`}
              account={account}
              isActive={
                supportedAccounts.length > 1 &&
                props.activeAccountId === account.id
              }
              onAccountSelected={(accountId) => {
                props.setIsShowing(false);
                props.onAccountChanged(accountId);
              }}
              testId={`account-radio-${
                index === supportedAccounts.length - 1 ? 'last' : index
              }`}
            />
          ))}
        {isIntegrationClient && unsupportedAccounts.length > 0 && (
          <>
            <MenuNonInteractiveArea>
              <div>
                <Text variant="body">{TEXT.unsupportedAccounts}</Text>
                <Text color="tertiary" variant="caption">
                  {TEXT.unsupportedAccountsDescription}
                </Text>
              </div>
            </MenuNonInteractiveArea>
            {unsupportedAccounts.map((account, index) => (
              <AccountOption
                key={`account-settings-popover-item-${account.id}`}
                account={account}
                disabled={true}
                isActive={false}
                testId={`unsupported-account-radio-${index}`}
              />
            ))}
          </>
        )}
      </MenuGroup>
    </>
  );
};
